import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { FlipbookModal, magazines } from "./FlipbookModal";
import { MousePointerClick } from "lucide-react";

export const SampleIssue = ({ openLead }) => {
  const [flipOpen, setFlipOpen] = useState(false);
  const [activeMag, setActiveMag] = useState(null);

  return (
    <section id="sample-issue" data-testid="sample-issue-section" className="py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta">
            Fictional sample
          </p>
          <h2 className="mt-5 max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight text-walnut sm:text-5xl">
            See exactly what you get.
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div data-testid="harbor-example" className="mt-14">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-terracotta sm:text-xs sm:tracking-[0.25em]">
              Fictional example: Harbor Studio. Company, people and results are
              illustrative, not a client case study.
            </p>
            <div className="mt-5 rounded-3xl border border-line bg-paper p-8 shadow-[0_10px_30px_rgba(42,32,28,0.06)] sm:p-10">
              <div className="grid gap-10 lg:grid-cols-2">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                    Recap email
                  </p>
                  <p className="mt-4 font-serif text-xl font-medium leading-snug tracking-tight text-walnut">
                    Subject: Harbor Studio | What we heard at all-hands
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-umber/85">
                    &ldquo;This all-hands focused on a clearer customer handoff and
                    sharing useful lessons across the team. The next step is to test
                    one shared handoff checklist before rolling it out more widely.
                    Thank you to everyone who shared examples and questions.&rdquo;
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                    Slack-ready highlights
                  </p>
                  <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-umber/85">
                    <li>- We&rsquo;re testing one shared customer handoff checklist.</li>
                    <li>- Each team will add a real example before the next check-in.</li>
                    <li>- We&rsquo;ll share what worked and what needs changing after the test.</li>
                  </ul>
                  <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                    Decisions and next steps
                  </p>
                  <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-umber/85">
                    <li>
                      - Draft the checklist. Owner: Maya. Due: before the next team
                      check-in.
                    </li>
                    <li>
                      - Collect feedback from the test. Owner: Jordan. Due: before the
                      following all-hands.
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-10 border-t border-line pt-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                  Culture moments
                </p>
                <div className="mt-4 grid gap-6 text-sm leading-relaxed text-umber/85 sm:grid-cols-3">
                  <p>
                    <span className="font-medium text-walnut">Recognition:</span>{" "}
                    &ldquo;Thank you to Maya for bringing teams together to make the
                    handoff easier to follow.&rdquo;
                  </p>
                  <p>
                    <span className="font-medium text-walnut">Value in action:</span>{" "}
                    &ldquo;Working openly: the team shared a rough first draft early so
                    others could improve it.&rdquo;
                  </p>
                  <p>
                    <span className="font-medium text-walnut">Pride moment:</span>{" "}
                    &ldquo;Different teams turned separate suggestions into one shared
                    next step.&rdquo;
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="mt-16 font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
            Optional add-on: digital magazine keepsake — fictional sample issue, not a
            real client. Ask about the magazine add-on.
          </p>
        </Reveal>

        <div className="mx-auto mt-8 grid max-w-4xl gap-6 sm:grid-cols-3">
          {magazines.map((mag, i) => (
            <motion.button
              key={mag.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -10 }}
              onClick={() => {
                setActiveMag(mag);
                setFlipOpen(true);
              }}
              data-testid={`magazine-cover-${mag.id}`}
              className="group relative block aspect-[707/1000] cursor-pointer overflow-hidden rounded-2xl border border-line text-left shadow-[0_10px_30px_rgba(42,32,28,0.08)]"
            >
              <img
                src={mag.pages[0].img}
                alt={`${mag.title} magazine cover`}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-walnut/80 via-transparent to-transparent" />
              <div className="absolute inset-x-0 bottom-0 px-5 pb-5">
                <p className="font-serif text-2xl italic leading-tight text-paper">{mag.title}</p>
                <div className="mt-2 flex items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/80">
                  <span>{mag.subtitle}</span>
                  <span className="shrink-0">{mag.pages.length} pages</span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-10 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-clay">
            <MousePointerClick className="h-4 w-4 text-terracotta" />
            Click a magazine to flip through every page
          </p>
        </Reveal>

        <FlipbookModal
          key={activeMag?.id}
          open={flipOpen}
          onClose={() => setFlipOpen(false)}
          openLead={openLead}
          magazine={activeMag}
        />
      </div>
    </section>
  );
};
